import { assoc, chain } from 'icepick';
import { customBlueprintModuleKey } from '../../model';
import {
	type SelectBlueprintAction,
	SELECT_BLUEPRINT,
	type CancelSelectBlueprintAction,
	CANCEL_SELECT_BLUEPRINT,
	type OpenBlueprintDialogAction,
	OPEN_BLUEPRINT_DIALOG,
} from '../actions/blueprint';
import {
	type SummaryLoadSuccessAction,
	RETRIEVE_SUCCESS,
	type SummaryLoadFailureAction,
	RETRIEVE_FAILURE,
} from '../actions/retrieve';
import {
	type DeleteRuleAction,
	DELETE_RULE,
	type DeleteSuccessAction,
	DELETE_SUCCESS,
	type DeleteFailureAction,
	DELETE_FAILURE,
	type SetShowDeleteDialogAction,
	SET_SHOW_DELETE_DIALOG,
} from '../actions/rule/delete';
import runAsUserReducer, { DEFAULT_RUN_AS_USER_UI_STATE } from './run-as-user/reducer';
import type { RunAsUserUIState } from './run-as-user/types';
import type { UIState } from './types';

const DEFAULT_UI_STATE: UIState = {
	isLoading: true,
	isBlueprintDialogOpen: false,
	deleteDialogOpenForRuleSetId: null,
	ruleSetBeingDeleted: null,
	selectedBlueprint: customBlueprintModuleKey,
	runAsUser: DEFAULT_RUN_AS_USER_UI_STATE,
};

type Actions =
	| SummaryLoadFailureAction
	| SummaryLoadSuccessAction
	| SetShowDeleteDialogAction
	| DeleteSuccessAction
	| DeleteFailureAction
	| SelectBlueprintAction
	| CancelSelectBlueprintAction
	| OpenBlueprintDialogAction
	| DeleteRuleAction;

const handleActions = (state: UIState = DEFAULT_UI_STATE, action: Actions): UIState => {
	switch (action.type) {
		case RETRIEVE_SUCCESS:
			return assoc(state, 'isLoading', false);
		case RETRIEVE_FAILURE: {
			const { error } = action.payload;
			return chain(state).assoc('isLoading', false).assoc('globalError', error).value();
		}
		case SET_SHOW_DELETE_DIALOG: {
			const { ruleSetId } = action.payload;
			return chain(state)
				.assoc('ruleSetBeingDeleted', null)
				.assoc('deleteDialogOpenForRuleSetId', ruleSetId)
				.value();
		}
		case DELETE_RULE: {
			const { ruleSetId } = action.payload;
			return assoc(state, 'ruleSetBeingDeleted', ruleSetId);
		}
		case DELETE_SUCCESS:
			return assoc(state, 'deleteDialogOpenForRuleSetId', null);
		case DELETE_FAILURE:
			return assoc(state, 'ruleSetBeingDeleted', null);
		case OPEN_BLUEPRINT_DIALOG:
			return assoc(state, 'isBlueprintDialogOpen', true);
		case SELECT_BLUEPRINT: {
			const { blueprintModuleKey } = action.payload;
			return assoc(state, 'selectedBlueprint', blueprintModuleKey);
		}
		case CANCEL_SELECT_BLUEPRINT: {
			return chain(state)
				.assoc('selectedBlueprint', customBlueprintModuleKey)
				.assoc('isBlueprintDialogOpen', false)
				.value();
		}
		default: {
			const _exhaustiveCheck: never = action;
			return state;
		}
	}
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, jira/import/no-anonymous-default-export
export default (state: UIState, action: any): UIState => {
	const runAsUserState: RunAsUserUIState = runAsUserReducer(state && state.runAsUser, action);
	const parentState: UIState = handleActions(state, action);
	return assoc(parentState, 'runAsUser', runAsUserState);
};
